import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { Subscription } from 'rxjs';

import { NotificationTime } from '@shared/restaurant/enums';
import { Notification, Party } from '@shared/party/models';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { ClockService } from '@tablesready/frontend-alerts-bar';

export interface MessagesActions<T> {
  mergeInboundOutbound: (party: T) => Notification[];
}

/**
 * How many messages the cell keeps as candidates. It shows as many as the row's height
 * allows, so the host must hand it more than a row could ever display — but not a party's
 * whole history, which would be dead DOM on every row.
 */
export const MESSAGES_CELL_CANDIDATES = 12;

/**
 * Messages table cell: the party's most recent messages, newest last. Sent, in-flight and
 * failed states each get a bullet icon; failed lines are emphasised and carry the failure
 * reason.
 *
 * How many messages show is decided by space, not by a count. The cell never adds height to
 * a row: it fills whatever the other columns already need and drops the oldest messages that
 * don't fit, so a chatty party looks no taller than a quiet one. The newest message is always
 * rendered — clipped rather than dropped — so the column is never blank while messages exist.
 */
@Component({
  selector: 'tablesready-waitlist-messages',
  templateUrl: './waitlist-messages.component.html',
  styleUrls: ['./waitlist-messages.component.scss'],
  standalone: false,
})
export class WaitlistMessagesComponent<T extends Party>
  implements OnInit, AfterViewInit, DoCheck, OnDestroy
{
  @Input() party?: T;
  @Input() timezone?: string;
  @Input() notificationTime = NotificationTime.Relative;
  @Input() index = 0;
  @Input() messagesActions?: MessagesActions<Party>;
  /**
   * Lay the messages out in normal flow instead of as a fixed-height table cell.
   *
   * In a column the cell must never add height to a row, so the lines are absolutely
   * positioned inside a box clipped to the row's height (see `fit()`), and an empty
   * cell still reserves two lines to keep the column aligned. A narrow-screen list
   * has no row height to clip to and no column to align with: the same treatment
   * would make the messages overlap the field below and leave a blank line when
   * there is nothing to show. In list layout the cell simply takes the height its
   * messages need — and none at all when there are none.
   */
  @Input() listLayout = false;

  /** Drives the flow-layout styles; see {@link listLayout}. */
  @HostBinding('class.msg-flow') get flowLayout(): boolean {
    return this.listLayout;
  }

  now = 0;
  notificationTimeEnum = NotificationTime;
  icon = AppIcon;
  color = AppIconColor;

  /**
   * Index of the first message that fits the row; everything before it is clipped out.
   * A binding rather than a direct style write, so it survives Angular re-rendering the
   * lines (which happens whenever a poll or socket update replaces the party object).
   */
  visibleFrom = 0;

  /**
   * Per line: whether any of its text is cut off — ellipsised meta, a token wider than the
   * column, or the newest message running past the row's height. Arms the full-text popover.
   */
  truncated: boolean[] = [];

  /**
   * Lines the newest message is clamped to when even it runs past the row's height, so it
   * ends on an ellipsis inside the row rather than being sliced mid-line by the cell's clip.
   * Null when it fits.
   */
  clampLines: number | null = null;

  @ViewChild('col') private readonly colRef?: ElementRef<HTMLElement>;

  private readonly subscription = new Subscription();
  private resizeObserver?: ResizeObserver;
  private frame = 0;
  /** Cheap fingerprint of the rendered messages, so DoCheck only re-measures on real changes. */
  private signature = '';

  constructor(
    private readonly clockService: ClockService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscription.add(this.clockService.clock$.subscribe((now) => (this.now = now)));
  }

  ngAfterViewInit(): void {
    // The row's height is the budget; it changes when a neighbouring cell reflows, when the
    // column is resized, and when the viewport changes.
    const row = this.host.nativeElement.closest('tr');
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
      if (row) {
        this.resizeObserver.observe(row);
      }
    });
    this.scheduleFit();
  }

  ngDoCheck(): void {
    const messages = this.displayMessages;
    const last = messages[messages.length - 1];
    const signature = `${messages.length}|${last?.time ?? ''}|${last?.status_sent ?? ''}`;
    if (signature !== this.signature) {
      this.signature = signature;
      this.scheduleFit();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.frame) {
      cancelAnimationFrame(this.frame);
    }
    this.subscription.unsubscribe();
  }

  /**
   * Candidate messages, newest last. Every message type is included — automatic ones like
   * confirmations too — and none are filtered by label; `fit()` decides how many are seen.
   */
  get displayMessages(): Notification[] {
    return (this.messagesActions?.mergeInboundOutbound(this.party as Party) ?? [])
      .slice(-MESSAGES_CELL_CANDIDATES);
  }

  /** Re-measure after the next paint, when the rendered lines reflect the latest state. */
  private scheduleFit(): void {
    if (this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.fit();
    });
  }

  /**
   * Work out the newest run of whole messages that fits the row's existing height.
   *
   * The lines are absolutely positioned, so what we show can never change the row height and
   * re-trigger the observer. Measuring needs every line laid out, so clipped ones are briefly
   * un-hidden inline; the inline style is cleared again and {@link visibleFrom} — a binding —
   * carries the result.
   */
  private fit(): void {
    const col = this.colRef?.nativeElement;
    if (!col) {
      return;
    }
    const lines = Array.from(col.children) as HTMLElement[];
    if (!lines.length) {
      col.style.height = '';
      this.setTruncated([]);
      this.setClamp(null);

      return;
    }

    // Measure every line at its full height: un-hide the clipped ones (inline style beats
    // .msgline--clipped) and lift the newest message's clamp.
    col.classList.add('msgcol--measuring');
    lines.forEach((line) => (line.style.display = 'grid'));
    const heights = lines.map((line) => line.offsetHeight);
    const truncated = lines.map((line) => this.overflowsWidth(line));
    lines.forEach((line) => (line.style.display = ''));
    col.classList.remove('msgcol--measuring');

    // List layout: the cell is in normal flow and every message is shown, so there
    // is no row-height budget to fit and nothing to clip.
    if (this.listLayout) {
      col.style.height = '';
      if (this.visibleFrom !== 0) {
        this.visibleFrom = 0;
        this.cdr.markForCheck();
      }
      this.setTruncated(truncated);
      this.setClamp(null);

      return;
    }

    const available = this.rowContentHeight();
    col.style.height = `${available}px`;

    // Walk from the newest backwards, keeping whole messages while they fit. The newest is
    // always kept even when the row is too short for it — better a clipped message than an
    // empty column.
    let used = 0;
    let firstVisible = lines.length - 1;
    for (let i = lines.length - 1; i >= 0; i--) {
      if (used + heights[i] > available && i < lines.length - 1) {
        break;
      }
      used += heights[i];
      firstVisible = i;
    }

    // Only the newest message can be kept past the budget, so it's the one clipped at the
    // bottom: clamp it to the whole lines that fit, so it ends on an ellipsis.
    const newest = lines[lines.length - 1];
    let clamp: number | null = null;
    if (used > available) {
      truncated[lines.length - 1] = true;
      const room = available - (used - heights[lines.length - 1]);
      const lineHeight = parseFloat(getComputedStyle(newest).lineHeight) || newest.offsetHeight;
      clamp = Math.max(1, Math.floor(room / lineHeight));
    }

    if (firstVisible !== this.visibleFrom) {
      this.zone.run(() => {
        this.visibleFrom = firstVisible;
        this.cdr.markForCheck();
      });
    }
    this.setTruncated(truncated);
    this.setClamp(clamp);
  }

  /** Whether a line's text or its ellipsised meta run is wider than the space it has. */
  private overflowsWidth(line: HTMLElement): boolean {
    const text = line.querySelector<HTMLElement>('.msgline-text');
    const meta = line.querySelector<HTMLElement>('.msgline-meta');

    return (
      (text !== null && text.scrollWidth > text.clientWidth) ||
      (meta !== null && meta.scrollWidth > meta.clientWidth)
    );
  }

  private setTruncated(next: boolean[]): void {
    if (
      next.length === this.truncated.length &&
      next.every((value, i) => value === this.truncated[i])
    ) {
      return;
    }
    this.zone.run(() => {
      this.truncated = next;
      this.cdr.markForCheck();
    });
  }

  private setClamp(next: number | null): void {
    if (next === this.clampLines) {
      return;
    }
    this.zone.run(() => {
      this.clampLines = next;
      this.cdr.markForCheck();
    });
  }

  /** The row's height minus this cell's own padding — the space the messages may occupy. */
  private rowContentHeight(): number {
    const cell = this.host.nativeElement.closest('td');
    const row = this.host.nativeElement.closest('tr');
    if (!cell || !row) {
      return Number.POSITIVE_INFINITY; // not in a table: never clip
    }
    const style = getComputedStyle(cell);
    const chrome =
      parseFloat(style.paddingTop) +
      parseFloat(style.paddingBottom) +
      parseFloat(style.borderTopWidth) +
      parseFloat(style.borderBottomWidth);

    return Math.max(0, row.getBoundingClientRect().height - chrome);
  }
}
